// University of South Carolina (Columbia): public college page (#2652),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - South Carolina's Common Data Set 2025-2026 (Office of Institutional
//    Research PDF): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C21/C22 plans. The C21
//    early decision block is empty and C22 gives an October 15 early action
//    date with no counts.
//  - Undergraduate Admissions pages: Freshman Applicants (deadlines, test
//    policy, review, decision types), Test-Optional FAQs and Honors College
//    and Scholars, apply. The deadlines carry no year label; they are the
//    pages as posted for the application that is open now (Fall 2027 entry:
//    the same pages name a Spring 2028 Verto semester and the March 2027 Top
//    Scholar Weekend).
//  - School of Music, Undergraduate Admissions (application and audition).
//  - Financial Aid and Scholarships, Cost to Attend: the 2026-27 budget for
//    undergraduates on campus, resident and non-resident. It matches the CDS
//    G1/G5 items and adds a weighted average of program fees.
//  - College Scorecard, school id 218663 (net price by family income), read
//    from the Scorecard's own school page because the shared DEMO_KEY was
//    over its rate limit; the figures are the API's `latest` fields.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'South Carolina Common Data Set 2025-2026',
        // The 2025-2026 PDF itself is served over http only (oiraa.dw.sc.edu);
        // the link is the university's https page that lists it.
        url: 'https://sc.edu/about/offices_and_divisions/institutional_research_assessment_and_analytics/institutional_effectiveness/common_data_set/index.php',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'South Carolina Undergraduate Admissions, Freshman Applicants',
        url: 'https://sc.edu/about/offices_and_divisions/undergraduate_admissions/apply/for_freshmen/index.php',
        read: '2026-10-01',
    },
    tests: {
        label: 'South Carolina Undergraduate Admissions, Test-Optional FAQs',
        url: 'https://sc.edu/about/offices_and_divisions/undergraduate_admissions/apply/for_freshmen/test_optional/index.php',
        read: '2026-10-01',
    },
    honors: {
        label: 'South Carolina Undergraduate Admissions, Honors College and Scholars: How to Apply',
        url: 'https://sc.edu/about/offices_and_divisions/undergraduate_admissions/honors_and_scholars_programs/apply/index.php',
        read: '2026-10-01',
    },
    music: {
        label: 'South Carolina School of Music, Undergraduate Admissions',
        url: 'https://sc.edu/study/colleges_schools/music/apply/undergraduate_admissions/index.php',
        read: '2026-10-01',
    },
    costs: {
        label: 'South Carolina Financial Aid and Scholarships, Cost to Attend',
        url: 'https://sc.edu/about/offices_and_divisions/financial_aid/cost_and_aid/cost_to_attend/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?218663-University-of-South-Carolina-Columbia',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-south-carolina',
    kbId: 'university_of_south_carolina',
    name: 'University of South Carolina',
    shortName: 'South Carolina',
    aliases: ['UofSC', 'USC Columbia', 'Gamecocks'],
    location: 'Columbia, SC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (South Carolina brand toolbox); decoration only.
    branding: {
        primary: '#73000A',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_south_carolina_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 59630,
        admitted: 33695,
        enrolled: 7818,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 14102, admitted: 10065 },
            { group: 'Out-of-state', applied: 44911, admitted: 23200 },
            { group: 'International', applied: 617, admitted: 430 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'South Carolina\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'South Carolina has no early decision plan. The Common Data Set gives the October 15 early action deadline but no early action counts, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to the university and choose a major, and your choice of major is part of the review because some majors have limited room. You can name a second major. Some programs ask for more:',
        rows: [
            { school: 'School of Music', requirement: 'Every music applicant also completes the School of Music application and an in-person audition or interview. You must be admitted by both the university and the School of Music.', source: 'music' },
            { school: 'South Carolina Honors College', requirement: 'A separate Honors College application, due November 15, with two more essays and one letter of recommendation (the letter is due December 1).', source: 'honors' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'December 1' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'Mid-December', binding: false },
            { plan: 'Honors College and Top Scholars', deadline: 'November 15', decision: 'Mid-February for the Honors College and Top Scholar awards', binding: false },
            { plan: 'Regular Decision', deadline: 'December 1', decision: 'Mid-March', binding: false },
        ],
        note: 'Any outstanding items on your USC Admissions Portal, test scores included, are due November 1 for Early Action, December 1 for the Honors College deadline and January 15 for Regular Decision. That is also the last day to change whether you want your scores considered.',
        source: 'firstYear',
        binding: 'None of these plans is binding: admitted freshmen have until May 1 to decide whether to enroll.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'South Carolina is test-optional through the fall 2027 term. On the application you choose whether you want your SAT or ACT scores considered. If you say yes, they are read and superscored; if you say no, any scores you sent are not reviewed. Applying without scores is no disadvantage.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'South Carolina has set its test-optional policy only through fall 2027 so far. It has not said whether students entering in fall 2028 must send SAT or ACT scores.', source: 'tests' },
        ],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [12288, 38098] },
            { item: 'Tech fee', amounts: [400, 400] },
            { item: 'Program fees (weighted average)', amounts: [1580, 1580] },
            { item: 'Housing (on campus)', amounts: [11432, 11432] },
            { item: 'Food', amounts: [5902, 5902] },
            { item: 'Books and supplies', amounts: [1522, 1522] },
            { item: 'Personal', amounts: [4790, 4790] },
            { item: 'Transportation', amounts: [2670, 2670] },
        ],
        totals: [40584, 66394],
        note: 'For undergraduates on the Columbia campus taking 12 hours a term and living on campus. South Carolina says your costs vary with your major and your housing and food choices.',
        source: 'costs',
        netPrice: {
            average: 22197,
            byIncome: [
                { income: '$0 to $30,000', amount: 13832 },
                { income: '$30,001 to $48,000', amount: 14286 },
                { income: '$48,001 to $75,000', amount: 17357 },
                { income: '$75,001 to $110,000', amount: 23410 },
                { income: 'Over $110,000', amount: 28504 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from South Carolina\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
